import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';

const OWNER_EMAIL = 'arthur.noal@gmail.com';

export interface LicenseApproval {
  approved_at: string;
  approved_by_name: string | null;
  licensed_until: string;
}

export interface SystemLockState {
  locked: boolean;
  message: string;
  locked_at: string | null;
  locked_by: string | null;
  locked_by_name: string | null;
  auto_lock_enabled: boolean;
  due_day: number;
  licensed_until: string | null;
  last_approved_at: string | null;
  last_approved_by_name: string | null;
  approval_history: LicenseApproval[];
}

const DEFAULT_STATE: SystemLockState = {
  locked: false,
  message: 'Sistema bloqueado por questões de licenciamento. Entre em contato com o administrador do sistema.',
  locked_at: null,
  locked_by: null,
  locked_by_name: null,
  auto_lock_enabled: false,
  due_day: 30,
  licensed_until: null,
  last_approved_at: null,
  last_approved_by_name: null,
  approval_history: [],
};

/** Retorna a data (YYYY-MM-DD) do dia de vencimento no mês da data informada, limitado ao último dia do mês. */
function dueDateInMonth(year: number, month: number, dueDay: number): string {
  const lastDay = new Date(year, month + 1, 0).getDate();
  const day = Math.min(Math.max(dueDay, 1), lastDay);
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

export function todayISO(): string {
  const now = new Date();
  return dueDateInMonth(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Próximo vencimento: a partir da validade atual (ou de hoje, se vencida/ausente) + 1 mês no dia configurado. */
export function nextDueDate(currentUntil: string | null, dueDay: number): string {
  const today = todayISO();
  const base = currentUntil && currentUntil >= today ? currentUntil : today;
  const [y, m] = base.split('-').map(Number);
  const refMonth = m - 1 + 1; // mês seguinte
  const year = y + Math.floor(refMonth / 12);
  const month = refMonth % 12;
  return dueDateInMonth(year, month, dueDay);
}

export function daysUntil(dateISO: string | null): number | null {
  if (!dateISO) return null;
  const [y, m, d] = dateISO.split('-').map(Number);
  const target = new Date(y, m - 1, d).getTime();
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((target - start) / 86400000);
}

export function useSystemLock() {
  const { user, profile } = useAuth();
  const [state, setState] = useState<SystemLockState>(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);

  const isOwner = !!user?.email && user.email.toLowerCase() === OWNER_EMAIL;

  const fetchLock = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('config_value')
        .eq('config_key', 'system_lock')
        .maybeSingle();
      if (error) throw error;
      if (data?.config_value) {
        setState({ ...DEFAULT_STATE, ...(data.config_value as any) });
      }
    } catch (err) {
      logger.error('Erro ao buscar estado de bloqueio do sistema', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLock();
    const channel = supabase
      .channel('system_lock_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'system_config', filter: 'config_key=eq.system_lock' },
        () => fetchLock(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchLock]);

  const setLock = useCallback(
    async (locked: boolean, message?: string) => {
      if (!isOwner || !user) {
        throw new Error('Apenas o proprietário do sistema pode alterar o bloqueio.');
      }
      const next: SystemLockState = {
        ...state,
        locked,
        message: message ?? state.message,
        locked_at: locked ? new Date().toISOString() : null,
        locked_by: locked ? user.id : null,
        locked_by_name: locked ? profile?.nome ?? user.email ?? null : null,
      };
      const { error } = await supabase
        .from('system_config')
        .update({ config_value: next as any, updated_at: new Date().toISOString() })
        .eq('config_key', 'system_lock');
      if (error) throw error;
      setState(next);
      return next;
    },
    [isOwner, user, profile, state],
  );

  const persist = useCallback(
    async (next: SystemLockState) => {
      if (!isOwner) throw new Error('Apenas o proprietário do sistema pode alterar a licença.');
      const { error } = await supabase
        .from('system_config')
        .update({ config_value: next as any, updated_at: new Date().toISOString() })
        .eq('config_key', 'system_lock');
      if (error) throw error;
      setState(next);
      return next;
    },
    [isOwner],
  );

  const setAutoLock = useCallback(
    async (enabled: boolean) => {
      const licensed_until =
        enabled && !state.licensed_until
          ? nextDueDate(null, state.due_day)
          : state.licensed_until;
      return persist({ ...state, auto_lock_enabled: enabled, licensed_until });
    },
    [persist, state],
  );

  const setDueDay = useCallback(
    async (day: number) => persist({ ...state, due_day: Math.min(Math.max(day, 1), 31) }),
    [persist, state],
  );

  const approveRenewal = useCallback(
    async (customUntil?: string) => {
      const licensed_until = customUntil || nextDueDate(state.licensed_until, state.due_day);
      const approval: LicenseApproval = {
        approved_at: new Date().toISOString(),
        approved_by_name: profile?.nome ?? user?.email ?? null,
        licensed_until,
      };
      return persist({
        ...state,
        licensed_until,
        last_approved_at: approval.approved_at,
        last_approved_by_name: approval.approved_by_name,
        approval_history: [approval, ...(state.approval_history || [])].slice(0, 12),
      });
    },
    [persist, state, profile, user],
  );

  const updateMessage = useCallback(
    async (message: string) => {
      if (!isOwner) throw new Error('Apenas o proprietário pode alterar a mensagem.');
      const next = { ...state, message };
      const { error } = await supabase
        .from('system_config')
        .update({ config_value: next as any, updated_at: new Date().toISOString() })
        .eq('config_key', 'system_lock');
      if (error) throw error;
      setState(next);
    },
    [isOwner, state],
  );

  const daysRemaining = daysUntil(state.licensed_until);
  const expired =
    state.auto_lock_enabled && !!state.licensed_until && (daysRemaining ?? 0) < 0;
  const effectiveLocked = state.locked || expired;

  return {
    ...state,
    loading,
    isOwner,
    daysRemaining,
    expired,
    effectiveLocked,
    setLock,
    setAutoLock,
    setDueDay,
    approveRenewal,
    updateMessage,
    refetch: fetchLock,
  };
}